import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Field from '../../../Common/Component/Field';
import SelectSearch from '../../../Common/Component/SelectSearch';
import { EJE_THEME } from '../../../Common/Constant';
import * as request from '../../../Common/Util/Request';
import { uploadInstructorImage } from '../../../Common/Util/Request';
import './index.scss';

function emptyCareer() {
  return { text: '', sort_order: 0 };
}

function InstructorNew() {
  const { id } = useParams();
  const [origin, setOrigin] = useState(null);
  const editing = useMemo(() => !!origin, [origin]);

  const [name, setName] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [image, setImage] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  const [tutorId, setTutorId] = useState('');
  const [tutorOptions, setTutorOptions] = useState([]);
  const [careers, setCareers] = useState([emptyCareer()]);
  const careerRefs = useRef([]);

  useEffect(() => {
    request
      .getAdminTutors()
      .then((res) => setTutorOptions(res?.data?.tutors ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    request
      .getAdminInstructor(id)
      .then((res) => setOrigin(res?.data?.instructor ?? res?.data ?? res))
      .catch(() => {
        Swal.fire({
          html: `<div style="margin-top:20px;font-weight:bold;">강사 정보를 불러오지 못했습니다.</div>`,
          icon: 'error',
        });
      });
  }, [id]);

  useEffect(() => {
    if (!origin) return;
    setName(origin.name || '');
    setSpecialty(origin.specialty || '');
    setImage(origin.image_profile || origin.profileImageUrl || '');
    setIsVisible(origin.is_visible ?? origin.isVisible ?? true);
    setTutorId(origin.tutorId ? String(origin.tutorId) : '');
    const items = origin.careerItems || origin.careers || [];
    setCareers(
      items.length ? items.map((it, i) => ({ text: it.text || '', sort_order: it.sort_order ?? i })) : [emptyCareer()],
    );
  }, [origin]);

  const onChangeCareerText = useCallback((index, value) => {
    setCareers((prev) => prev.map((c, i) => (i === index ? { ...c, text: value } : c)));
  }, []);

  const onBoldClick = useCallback(
    (index) => {
      const el = careerRefs.current[index];
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const text = careers[index].text;
      const newText = text.slice(0, start) + '**' + text.slice(start, end) + '**' + text.slice(end);
      onChangeCareerText(index, newText);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + 2, end + 2);
      });
    },
    [careers, onChangeCareerText],
  );

  const onAddCareer = useCallback(() => {
    setCareers((prev) => [...prev, { ...emptyCareer(), sort_order: prev.length }]);
  }, []);

  const onRemoveCareer = useCallback((index) => {
    setCareers((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const onClickSave = useCallback(() => {
    if (!name.trim() || !specialty.trim()) {
      Swal.fire({
        html: `<div style="margin-top:20px;font-weight:bold;">이름, 전문분야를 입력해주세요.</div>`,
        toast: true,
        position: 'center',
        timer: 1500,
        timerProgressBar: true,
        showConfirmButton: true,
        icon: 'error',
      });
      return;
    }

    const payload = {
      name: name.trim(),
      specialty: specialty.trim(),
      isVisible,
      tutorId: tutorId ? parseInt(tutorId, 10) : null,
      careerItems: careers
        .filter((c) => c.text.trim())
        .map((c, i) => ({ text: c.text.trim(), isHighlight: false, sortOrder: i })),
    };
    if (image) payload.profileImageUrl = image;

    const onSuccess = (msg) => {
      window.opener && window.opener.location.reload();
      Swal.fire({
        html: `<div style="margin-top:20px;font-weight:bold;">${msg}</div>`,
        position: 'center',
        icon: 'success',
      }).then(() => {
        window.exitFlag = true;
        window.self.close();
      });
    };

    const onFailure = (msg) => () => {
      Swal.fire({
        html: `<div style="margin-top:20px;font-weight:bold;">${msg}</div>`,
        toast: true,
        position: 'center',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
        icon: 'error',
      });
    };

    if (editing) {
      request
        .updateInstructor(origin.id, payload)
        .then(() => onSuccess('성공적으로 수정되었습니다.'))
        .catch(onFailure('수정에 실패했습니다.'));
    } else {
      request
        .createInstructor(payload)
        .then(() => onSuccess('성공적으로 생성되었습니다.'))
        .catch(onFailure('생성에 실패했습니다.'));
    }
  }, [editing, origin, name, specialty, image, isVisible, tutorId, careers]);

  const onClickRemove = useCallback(() => {
    Swal.fire({
      title: '정말 삭제하시겠습니까?',
      text: '삭제한 데이터는 복구할 수 없습니다.',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
      preConfirm: () =>
        request.deleteInstructor(origin.id).then(() => {
          window.opener && window.opener.location.reload();
          window.exitFlag = true;
          window.self.close();
        }),
    });
  }, [origin]);

  const onClickCancel = useCallback(() => {
    Swal.fire({
      html: `<div style="margin-top:20px;font-weight:bold;">저장하지 않고 나가시겠습니까?</div>`,
      toast: true,
      position: 'center',
      showConfirmButton: true,
      showCancelButton: true,
      icon: 'warning',
    }).then(({ isConfirmed }) => {
      if (isConfirmed) {
        window.exitFlag = true;
        window.close();
      }
    });
  }, []);

  return (
    <ThemeProvider theme={EJE_THEME}>
      <div className="instructor-new">
        <div className="instructor-new__header">
          <Typography variant="h6" fontWeight={700} color="#1a1a1a">
            {editing ? '강사 수정' : '강사 등록'}
          </Typography>
          <Typography variant="body2" color="#888" mt={0.25}>
            강사 소개 페이지에 노출할 강사 정보를 {editing ? '수정' : '등록'}합니다.
          </Typography>
        </div>

        {/* 기본 정보 */}
        <Paper elevation={0} className="instructor-new__card">
          <Typography variant="subtitle1" fontWeight={700} color="#1a1a1a" mb={2.5}>
            기본 정보
          </Typography>
          <Stack spacing={2.5}>
            <Box>
              <Typography className="instructor-new__label">이름 *</Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="강사 표시명을 입력하세요."
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ maxWidth: 420 }}
              />
            </Box>

            <Box>
              <Typography className="instructor-new__label">전문분야 *</Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="예: 자기소개서 & 면접"
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                sx={{ maxWidth: 420 }}
              />
              <Typography variant="caption" color="#aaa" mt={0.75} display="block">
                띄어쓰기 포함 <strong style={{ color: '#888' }}> &amp; </strong> 또는{' '}
                <strong style={{ color: '#888' }}>,</strong> 기준으로 태그가 나뉩니다. &nbsp;A&amp;R처럼 공백 없이
                붙어있으면 하나의 태그로 처리됩니다.
              </Typography>
            </Box>

            <Box>
              <Typography className="instructor-new__label">프로필 이미지</Typography>
              <Field.Image value={image} onChange={setImage} uploadFn={uploadInstructorImage} />
            </Box>

            <Box>
              <Typography className="instructor-new__label">노출 여부</Typography>
              <FormControlLabel
                control={<Switch color="pink" checked={!!isVisible} onChange={(e) => setIsVisible(e.target.checked)} />}
                label={
                  <Typography variant="body2" fontWeight={600} color={isVisible ? '#00875a' : '#999'}>
                    {isVisible ? '노출' : '미노출'}
                  </Typography>
                }
                sx={{ ml: 0 }}
              />
            </Box>

            <Box>
              <Typography className="instructor-new__label">강사 연결 (강의 노출)</Typography>
              <div className="instructor-new__tutor-select">
                <SelectSearch
                  options={[
                    { name: '— 매핑 없음 (대표 강의 미노출) —', value: '' },
                    ...tutorOptions.map((t) => ({ name: `${t.name} (id: ${t.id})`, value: String(t.id) })),
                  ]}
                  value={tutorId}
                  onChange={(val) => setTutorId(val)}
                  placeholder="강사(tutor) 검색"
                />
              </div>
              <Typography variant="caption" color="#aaa" mt={0.75} display="block">
                선택 시 해당 강사의 강의 중 수강생 수 기준 상위 4개가 대표 강의로 자동 노출됩니다.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* 경력 항목 */}
        <Paper elevation={0} className="instructor-new__card">
          <Typography variant="subtitle1" fontWeight={700} color="#1a1a1a" mb={2.5}>
            경력 항목
          </Typography>
          <Stack spacing={1}>
            {careers.map((career, index) => (
              <Stack key={index} direction="row" alignItems="flex-start" spacing={1}>
                <Typography
                  variant="caption"
                  color="#bbb"
                  sx={{ minWidth: 20, textAlign: 'center', pt: 1.25, fontWeight: 600 }}
                >
                  {index + 1}
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Box className="instructor-new__career-toolbar">
                    <Tooltip title="선택 텍스트를 굵게 (**bold**)">
                      <button
                        type="button"
                        className="instructor-new__bold-btn"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          onBoldClick(index);
                        }}
                      >
                        B
                      </button>
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="경력 내용을 입력하세요."
                    value={career.text}
                    onChange={(e) => onChangeCareerText(index, e.target.value)}
                    inputRef={(el) => (careerRefs.current[index] = el)}
                    sx={{ '& .MuiOutlinedInput-root': { borderTopLeftRadius: 0, borderTopRightRadius: 0 } }}
                  />
                </Box>
                <Tooltip title="삭제">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onRemoveCareer(index)}
                      disabled={careers.length === 1}
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={onAddCareer}
              variant="outlined"
              size="small"
              sx={{
                alignSelf: 'flex-start',
                mt: 0.5,
                color: '#888',
                borderColor: '#ddd',
                fontSize: '0.8125rem',
                borderRadius: '6px',
                '&:hover': { borderColor: '#bbb', background: '#fafafa' },
              }}
            >
              항목 추가
            </Button>
          </Stack>
        </Paper>

        {/* 하단 버튼 */}
        <Box className="instructor-new__actions">
          <Button
            variant="outlined"
            color="error"
            onClick={onClickRemove}
            disabled={!editing}
            sx={{ borderRadius: '8px' }}
          >
            삭제
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={onClickCancel}
              sx={{ borderRadius: '8px', color: '#555', borderColor: '#ddd', '&:hover': { borderColor: '#bbb' } }}
            >
              취소
            </Button>
            <Button
              variant="contained"
              color="pink"
              onClick={onClickSave}
              sx={{ borderRadius: '8px', fontWeight: 700, px: 3 }}
            >
              저장
            </Button>
          </Stack>
        </Box>
      </div>
    </ThemeProvider>
  );
}

export default InstructorNew;
